import { useSearchParams } from 'react-router-dom'
import classNames from 'classnames'
import { CheckCircle, EditPencil as EditPencilIcon, PasteClipboard, Undo } from 'iconoir-react'
import { capitalize } from 'lodash'
import moment from 'moment'
import { ReactElement, useEffect, useState } from 'react'
import { useQuery } from 'urql'
import Spinner from '../Spinner'
import { EditTaskModal } from '../tasks/EditTaskModal'
import { DELETE_TASK_MUTATION, TASK_OWNER_QUERY, TASK_QUERY } from '../tasks/graphql'
import { Tooltip } from '../Tooltip'
import { ActivityCard } from './ActivityCard'
import { KeyValueDrawer } from './KeyValueDrawer'
import { formatDate } from 'app/date.utils'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { TaskJson } from 'app/types/entities'
import type { UserJson } from 'app/types/entities'
import { gqlMutation } from 'app/lib/graphqlClient'
import { overdueDays, taskDueState } from 'app/lib/taskDueDate'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { useWorkspace } from 'app/utils'
import { useTaskStore } from 'app/stores/StoreContext'

type Props = {
  activity: ActivityJson
  alwaysOpen?: boolean
  contact?: ContactJson
  isFirst?: boolean
  isLast?: boolean
  singleActivity?: boolean
  handleEdit?: (activity: ActivityJson) => void
  handleDelete?: () => void
  onTaskUpdated?: (task: TaskJson) => void
}

export function TaskCard({
  activity,
  alwaysOpen,
  contact,
  isFirst,
  isLast,
  singleActivity,
  handleEdit,
  handleDelete,
  onTaskUpdated,
}: Props): ReactElement {
  const workspace = useWorkspace()
  const taskStore = useTaskStore()
  const [searchParams] = useSearchParams()
  const [highlightActive, setHighlightActive] = useState(false)

  // Primary fetch. Paused until we have an activity.id so we don't fire a query
  // with a placeholder variable on first render.
  const [taskQuery] = useQuery<{ task: TaskJson | null }>({
    query: TASK_QUERY,
    variables: { id: activity.id ?? `` },
    pause: !activity.id,
  })
  const task: TaskJson | undefined = taskQuery.data?.task ?? undefined

  // Dependent fetch. `pause` waits for the task (and its ownerId) before firing
  // — the urql doc cache + mutation invalidation keep this in sync with status
  // edits, so we don't need a local `setTask` to mirror it.
  const [ownerQuery] = useQuery<{ user: UserJson | null }>({
    query: TASK_OWNER_QUERY,
    variables: { id: task?.ownerId ?? `` },
    pause: !task?.ownerId,
  })
  const owner: UserJson | undefined = ownerQuery.data?.user ?? undefined

  // Due dates are calendar days, not instants. See app/lib/taskDueDate.
  const dueState = taskDueState({ dueDate: task?.dueDate, status: task?.status })

  const isLoading = taskQuery.fetching && !task
  const fetchError =
    !taskQuery.fetching && (taskQuery.error || taskQuery.data?.task === null) ? `Task not found` : undefined

  useEffect(() => {
    const highlightId = searchParams.get(`highlightTaskId`)
    if (task?.id && highlightId === task.id) {
      setHighlightActive(true)
      const timeoutId = setTimeout(() => setHighlightActive(false), 5000)
      return () => clearTimeout(timeoutId)
    }
  }, [task?.id, searchParams])

  const getDrawerContent = (isPreview: boolean) => {
    const innerClasses = classNames(
      `w-full items-center rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm`,
      highlightActive && `highlight-pulse-inner border-blue-600`,
    )
    const content = (
      <div className={innerClasses}>
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Spinner size={4} />
          </div>
        ) : task ? (
          <>
            <div className="flex items-center justify-between">
              <p className="text-subtle text-ui-xs">Task Details</p>
              <button
                className={classNames(
                  `btn btn-xxs border`,
                  task.status === `open` ? `border border-success-default text-success-default` : `text-strong`,
                )}
                onClick={async (e) => {
                  e.stopPropagation()
                  await onCheck(task.id!, task.status === `open`)
                }}
              >
                {task.status === `open` ? (
                  <CheckCircle className="h-2.5 w-2.5" strokeWidth={3} />
                ) : (
                  <Undo className="h-2.5 w-2.5" strokeWidth={3} />
                )}
                {task.status === `open` ? `Mark as Complete` : `Mark as Open`}
              </button>
            </div>
            <div className="my-3 flex w-full flex-col text-body-xs text-strong">
              <span className="text-subtle">Description</span>
              <div className="mt-0.5 flex items-center gap-x-1 text-body-xs text-strong">
                {task?.description || `-`}
              </div>
            </div>
            <KeyValueDrawer
              map={{
                'Due Date': (
                  <div
                    className={classNames(
                      dueState === `overdue` ? `text-red-700` : `text-strong`,
                      `mt-0.5 break-words text-ui-xs`,
                    )}
                  >
                    {task?.dueDate ? formatDate({ value: task?.dueDate, dateOnly: true }) : `-`}
                  </div>
                ),
                'Linked Contact': contact?.name || `-`,
                Owner: owner?.name || `-`,
              }}
            />
          </>
        ) : (
          fetchError && <div className="w-full text-center text-strong">{capitalize(fetchError)}</div>
        )}
      </div>
    )

    if (!highlightActive) return content

    return <div className={classNames(`highlight-pulse-wrapper rounded-lg`)}>{content}</div>
  }

  const handleTaskUpdated = (updated?: TaskJson) => {
    // No local setTask: the urql document cache invalidates the Task query
    // when the modal's updateTask mutation lands, so the next render picks
    // the new data up automatically. We still bubble the change up so the
    // parent (e.g. the activity feed) can react.
    if (updated) onTaskUpdated?.(updated)
  }

  const onDeleteTask = async (taskId: string) => {
    const { data, error } = await gqlMutation(DELETE_TASK_MUTATION, { id: taskId })
    if (error || !data?.deleteTask) {
      notifyError(`Failed to delete task${error?.message ? `: ${error.message}` : ``}`)
      return
    }
    notifySuccess(`Task deleted`)
    closeModal()
    // Remove the card from the activity feed (parent-supplied handler).
    handleDelete?.()
  }

  const getEditTaskForm = (editingTask: TaskJson) => (
    <EditTaskModal
      onTaskCreatedOrUpdated={handleTaskUpdated}
      onTaskDelete={onDeleteTask}
      editingTask={editingTask}
      defaultContact={contact}
    />
  )

  const onCheck = async (taskId: string, checked: boolean) => {
    const result = await taskStore.update(taskId, { status: checked ? `closed` : `open` })
    if (!result.error) {
      notifySuccess(`Task status updated`)
      // The mutation result drives the parent callback; the urql doc cache
      // handles refreshing our local task query for the in-card render.
      const updatedTask = (result.data as any)?.updateTask ?? { ...task, status: checked ? `closed` : `open` }
      onTaskUpdated?.(updatedTask)
    } else {
      notifyError(`Failed to update task status`)
    }
  }

  const getTaskStatus = () => {
    if (dueState === `overdue` && task?.dueDate) {
      const days = overdueDays(task.dueDate)
      return (
        <span className="rounded border bg-red-100 px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-red-700">
          {`Due ${days} day${days === 1 ? `` : `s`} ago`}
        </span>
      )
    } else if (dueState === `dueToday`) {
      return (
        <span className="rounded border bg-info-minimal px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-strong">
          {`Due today`}
        </span>
      )
    } else if (dueState === `upcoming`) {
      return (
        <span className="rounded border bg-gray-minimal px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-strong">
          Upcoming
        </span>
      )
    } else if (dueState === `completed`) {
      return (
        <span className="rounded border bg-green-50 px-1.5 py-0.5 text-xs font-medium not-italic leading-4 text-strong">
          Completed
        </span>
      )
    }
    return undefined
  }

  return (
    <ActivityCard
      activityDate={moment(activity?.activityDate).format(`YYYY-MM-DD`)}
      icon={
        <PasteClipboard
          className={classNames(
            `h-5 w-5 rounded-md p-1`,
            dueState === `overdue`
              ? `bg-red-800 text-on-color`
              : dueState === `dueToday`
                ? `bg-primary-600 text-on-color`
                : dueState === `completed`
                  ? `bg-green-600 text-on-color`
                  : `bg-fill-neutral text-on-color`,
          )}
          stroke="1.5"
        />
      }
      title={
        <div className="ml-1 flex flex-wrap items-center gap-x-2 text-ui-sm text-strong">
          {isLoading || !task ? (
            <div className="flex items-center justify-center">
              <Spinner size={4} />
            </div>
          ) : (
            <>
              <Tooltip
                placement="bottom"
                message={task?.title}
                className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap"
              >
                <span className="leading-normal">{task?.title}</span>
              </Tooltip>
            </>
          )}
        </div>
      }
      actions={
        !!workspace ? (
          <div className="flex gap-x-1 p-0.5">
            <Tooltip message="Edit Task">
              <EditPencilIcon
                className="h-3 w-3 cursor-pointer"
                strokeWidth={1}
                aria-label={`Edit ${activity.type}`}
                onClick={() =>
                  task &&
                  showModal(getEditTaskForm(task), {
                    title: `Edit task`,
                    isFullScreen: false,
                    overflowHidden: false,
                    closable: true,
                    removePadding: true,
                    useRadix: true,
                  })
                }
              />
            </Tooltip>
          </div>
        ) : undefined
      }
      preview={getDrawerContent(true)}
      drawer={getDrawerContent(false)}
      tags={getTaskStatus()}
      isFirst={isFirst}
      isLast={isLast}
      alwaysOpen={alwaysOpen}
      singleActivity={singleActivity}
    />
  )
}
